import { useEffect, useRef } from 'react';
import { Device, type Call } from '@twilio/voice-sdk';
import { useAuth } from '../store/auth';
import { useDeviceStore } from '../store/device';
import { fetchTwilioToken } from './tokenManager';

const REFRESH_LEAD_MS = 5 * 60_000;     // refresh 5 minutes before expiry
const RETRY_DELAY_MS = 30_000;          // FR-28: 30s between retries
const MAX_RETRIES = 3;                  // FR-28: up to 3 attempts

/**
 * Twilio Voice Device lifecycle + token-refresh hardening (FR-27/28/29).
 *
 *   FR-27: refresh in the background before expiry, no re-auth required
 *   FR-28: on failure, retry up to 3× at 30s intervals before alerting
 *   FR-29: during an active call, the system shall NEVER disconnect the
 *          agent due to token expiry — refresh seamlessly, and if the
 *          retries are exhausted, defer re-auth until the call ends.
 *
 * Implementation:
 *   - On mount: fetch token, build Device, register, schedule refresh.
 *   - Refresh path: setTimeout at T-300s + visibilitychange recompute on
 *     tab focus + Voice SDK 'tokenWillExpire' as a server-side safety net.
 *   - Refresh action: POST /token, call device.updateToken(token) which
 *     Twilio guarantees is non-disruptive to active calls.
 *   - Failure path: 3 retries at 30s. If still failing AND a call is
 *     active: leash to the call's 'disconnect' event and force re-auth
 *     only after the call ends. Otherwise force re-auth immediately.
 */
export function useDevice() {
  const userId = useAuth(s => s.user?.userId);
  const setReauthRequired = useAuth(s => s.setReauthRequired);
  const setDevice = useDeviceStore(s => s.setDevice);
  const markReady = useDeviceStore(s => s.markReady);
  const setIncoming = useDeviceStore(s => s.setIncoming);
  const setActive = useDeviceStore(s => s.setActive);

  const refreshTimer = useRef<number | undefined>(undefined);
  const retryCountRef = useRef(0);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    let device: Device | undefined;

    function clearTimers() {
      if (refreshTimer.current) {
        window.clearTimeout(refreshTimer.current);
        refreshTimer.current = undefined;
      }
    }

    function scheduleRefreshAt(expiresAt: number) {
      clearTimers();
      const msUntilRefresh = Math.max(60_000, expiresAt - Date.now() - REFRESH_LEAD_MS);
      refreshTimer.current = window.setTimeout(() => void refreshNow(), msUntilRefresh);
    }

    async function refreshNow(): Promise<void> {
      if (!device || cancelled) return;
      try {
        const { token, expiresAt } = await fetchTwilioToken();
        if (cancelled || !device) return;
        // Non-disruptive per Twilio docs; active calls remain connected (FR-29).
        device.updateToken(token);
        retryCountRef.current = 0;
        scheduleRefreshAt(expiresAt);
      } catch (err) {
        console.error('Twilio token refresh failed', err);
        retryCountRef.current += 1;
        if (retryCountRef.current < MAX_RETRIES) {
          // FR-28: retry up to 3 times at 30s intervals.
          refreshTimer.current = window.setTimeout(() => void refreshNow(), RETRY_DELAY_MS);
        } else {
          handleTerminalFailure();
        }
      }
    }

    function handleTerminalFailure() {
      const activeCall = useDeviceStore.getState().activeCall;
      if (activeCall) {
        // FR-29: never disconnect during an active call. Defer re-auth.
        console.warn('Token refresh exhausted; deferring re-auth until call ends');
        const onDone = () => {
          activeCall.removeListener('disconnect', onDone);
          setReauthRequired(true);
        };
        activeCall.on('disconnect', onDone);
      } else {
        setReauthRequired(true);
      }
    }

    function onVisibility() {
      if (document.visibilityState !== 'visible' || !device || cancelled) return;
      // Background tabs throttle setTimeout — recompute on focus and refresh
      // immediately if we've already passed the refresh lead.
      void (async () => {
        try {
          const { token, expiresAt } = await fetchTwilioToken();
          if (cancelled || !device) return;
          // If the token we just fetched is still ages away from expiry,
          // we may have refreshed unnecessarily — that's fine, it's cheap.
          device.updateToken(token);
          retryCountRef.current = 0;
          scheduleRefreshAt(expiresAt);
        } catch (err) {
          console.error('visibility-triggered token refresh failed', err);
          retryCountRef.current += 1;
          if (retryCountRef.current >= MAX_RETRIES) handleTerminalFailure();
        }
      })();
    }

    async function init() {
      const { token, expiresAt } = await fetchTwilioToken();
      if (cancelled) return;

      device = new Device(token, {
        logLevel: 1,
        edge: 'sydney',
      });

      device.on('incoming', (call: Call) => {
        const current = useDeviceStore.getState();
        if (current.activeCall) {
          call.reject();
          return;
        }
        current.setIncoming(call);

        call.on('cancel', () => {
          if (useDeviceStore.getState().incomingCall === call) setIncoming(undefined);
        });
        call.on('disconnect', () => {
          const s = useDeviceStore.getState();
          if (s.incomingCall === call) setIncoming(undefined);
          if (s.activeCall === call) setActive(undefined);
        });
        call.on('accept', () => {
          setIncoming(undefined);
          setActive(call);
        });
      });

      device.on('error', err => {
        console.error('Twilio Device error', err);
      });

      // Voice SDK fires this ~T-180s before the token expires as a server-
      // side safety net in case our scheduled refresh missed.
      device.on('tokenWillExpire', () => void refreshNow());

      await device.register();
      if (cancelled) {
        device.destroy();
        return;
      }

      setDevice(device);
      markReady(true);
      retryCountRef.current = 0;
      scheduleRefreshAt(expiresAt);
      document.addEventListener('visibilitychange', onVisibility);
    }

    init().catch(err => {
      console.error('Device init failed', err);
      markReady(true);
    });

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimers();
      if (device) {
        device.removeAllListeners();
        device.destroy();
      }
      setDevice(undefined);
      markReady(false);
      setIncoming(undefined);
      setActive(undefined);
    };
  }, [userId, setDevice, markReady, setIncoming, setActive, setReauthRequired]);
}
